import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CaretUpDown, User, Storefront, Buildings, ShieldCheck, Check,
  Bell, SignOut, ArrowsClockwise
} from '@phosphor-icons/react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CONFIG = {
  personal: { icon: User, color: '#00F0FF', label: 'Player' },
  shop: { icon: Storefront, color: '#9D4CDD', label: 'Shop' },
  publisher: { icon: Buildings, color: '#F59E0B', label: 'Publisher' },
  internal: { icon: ShieldCheck, color: '#EF4444', label: 'Admin' },
};

export default function GlobalContextSwitcher({ variant = 'sidebar' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { contexts, activeTenantId, activeContext, switchContext, pendingInvites, acceptInvite, declineInvite } = useTenant();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const config = TYPE_CONFIG[activeContext?.tenant_type] || TYPE_CONFIG.personal;
  const Icon = config.icon;

  const handleSwitch = async (tenantId, ctx) => {
    await switchContext(tenantId);
    setOpen(false);
    // Small delay to let React state propagate before navigating
    setTimeout(() => {
      if (ctx.tenant_type === 'shop' && ctx.entity_id) {
        navigate(`/shop/${ctx.entity_id}`);
      } else if (ctx.tenant_type === 'publisher' && ctx.entity_id) {
        navigate(`/publisher/${ctx.entity_id}`);
      } else if (ctx.tenant_type === 'internal') {
        navigate('/admin');
      } else {
        navigate('/discover');
      }
    }, 100);
  };

  const grouped = {};
  contexts.forEach(c => {
    if (!grouped[c.tenant_type]) grouped[c.tenant_type] = [];
    if (!grouped[c.tenant_type].find(x => x.tenant_id === c.tenant_id)) {
      grouped[c.tenant_type].push(c);
    }
  });

  const isSidebar = variant === 'sidebar';

  return (
    <div className="relative" ref={ref} data-testid="global-context-switcher">
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-3 w-full transition-all rounded-xl ${
          isSidebar
            ? 'px-3 py-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06]'
            : 'px-3 py-2 bg-[#0e0e1a] border border-[#1a1a2e] hover:border-[#2a2a4a]'
        }`}
        data-testid="context-switcher-trigger"
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${config.color}15` }}
        >
          <Icon size={18} weight="duotone" style={{ color: config.color }} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-white text-sm font-semibold truncate">
            {activeContext?.tenant_name || user?.name || 'Player'}
          </p>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: config.color }}>
            {config.label}
          </p>
        </div>
        {pendingInvites.length > 0 && (
          <span className="w-5 h-5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
            {pendingInvites.length}
          </span>
        )}
        <CaretUpDown size={16} className="text-[#555] flex-shrink-0" />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className={`absolute z-[60] w-80 bg-[#0a0a14] border border-[#1a1a2e] rounded-xl shadow-2xl overflow-hidden ${
                isSidebar ? 'left-0 top-full mt-2' : 'right-0 top-full mt-2'
              }`}
              data-testid="context-switcher-menu"
            >
              {/* Header */}
              <div className="px-4 py-3 border-b border-[#1a1a2e]">
                <div className="flex items-center gap-2">
                  <ArrowsClockwise size={14} className="text-[#555]" />
                  <p className="text-xs text-[#777] uppercase tracking-wider font-semibold">Switch Workspace</p>
                </div>
              </div>

              {/* Pending Invitations */}
              {pendingInvites.length > 0 && (
                <div className="px-3 py-3 border-b border-[#1a1a2e] bg-[#F59E0B]/[0.03]">
                  <p className="text-[10px] text-[#F59E0B] uppercase tracking-wider mb-2 flex items-center gap-1 font-semibold">
                    <Bell size={10} weight="fill" /> {pendingInvites.length} Pending Invitation{pendingInvites.length > 1 ? 's' : ''}
                  </p>
                  {pendingInvites.map(inv => (
                    <div key={inv.id} className="p-2.5 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/10 mb-1.5 last:mb-0">
                      <p className="text-white text-xs font-medium">{inv.tenant_name}</p>
                      <p className="text-[10px] text-[#777] mt-0.5">as {inv.role_code.replace(/_/g, ' ')} · from {inv.invited_by}</p>
                      <div className="flex gap-1.5 mt-2">
                        <button
                          onClick={() => { acceptInvite(inv.id); setOpen(false); }}
                          className="text-[10px] px-3 py-1 rounded-md bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 font-medium transition-colors"
                          data-testid={`accept-invite-${inv.id}`}
                        >Accept</button>
                        <button
                          onClick={() => declineInvite(inv.id)}
                          className="text-[10px] px-3 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 font-medium transition-colors"
                          data-testid={`decline-invite-${inv.id}`}
                        >Decline</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Context Groups */}
              <div className="max-h-[320px] overflow-y-auto">
                {Object.entries(grouped).map(([type, ctxs]) => {
                  const tc = TYPE_CONFIG[type] || TYPE_CONFIG.personal;
                  const TypeIcon = tc.icon;
                  return (
                    <div key={type} className="px-2 py-2">
                      <p className="text-[10px] uppercase tracking-wider px-2 mb-1.5 font-semibold" style={{ color: tc.color }}>
                        {tc.label}{ctxs.length > 1 ? 's' : ''}
                      </p>
                      {ctxs.map(ctx => {
                        const isActive = ctx.tenant_id === activeTenantId;
                        return (
                          <button
                            key={ctx.tenant_id}
                            onClick={() => handleSwitch(ctx.tenant_id, ctx)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                              isActive ? 'bg-white/[0.05]' : 'hover:bg-white/[0.03]'
                            }`}
                            data-testid={`context-option-${ctx.tenant_id}`}
                          >
                            <TypeIcon size={16} weight="duotone" style={{ color: tc.color }} />
                            <span className="text-white text-sm flex-1 truncate">{ctx.tenant_name}</span>
                            {ctx.roles?.[0] && (
                              <span className="text-[9px] text-[#555] capitalize">{ctx.roles[0].replace(/_/g, ' ')}</span>
                            )}
                            {isActive && <Check size={14} className="text-[#10B981] flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
